import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchAssessment } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";
const fmtDollars = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

/**
 * Read-only view of an existing assessment.  Reached from the SMS link
 * the contractor receives via the "TEXT ME THIS REPORT" button.
 * No questionnaire — just the results screen, framed as "your saved report".
 */
export default function AssessmentReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetchAssessment(id);
        setData(r);
      } catch (e) {
        setError(e?.response?.status === 404 ? "not_found" : "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const ctaToSignup = () => {
    const q = new URLSearchParams();
    const code = data?.discount_code || (data?.rep_slug === "thomas" ? "TT1" : data?.rep_slug?.toUpperCase());
    if (code) q.set("code", code);
    navigate(`/pricing?${q.toString()}`);
  };

  if (loading) {
    return <div style={pageWrap} data-testid="report-loading"><p style={{ color: "#5b5e69" }}>Loading your saved report…</p></div>;
  }
  if (error === "not_found" || !data) {
    return (
      <div style={pageWrap} data-testid="report-not-found">
        <h1 style={h1}>REPORT NOT FOUND</h1>
        <p style={{ color: "#5b5e69", fontSize: 16 }}>
          That report link is no longer valid. Want to start fresh? <a href="/assessment" style={{ color: NAVY, fontWeight: 700 }}>Run a new health-score assessment</a>.
        </p>
      </div>
    );
  }

  return (
    <div style={pageWrap} data-testid="assessment-report-page">
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>YOUR SAVED · REVENUE LEAKAGE REPORT</div>
        <h1 style={h1}>
          {(data.business_name || "YOUR BUSINESS").toUpperCase()}
        </h1>
        <p style={{ color: "#5b5e69", fontSize: 13, marginBottom: 18 }}>
          Saved on {new Date(data.created_at).toLocaleDateString("en-US", { dateStyle: "long" })} · Trade: {data.trade}
        </p>

        <div style={card}>
          {data.category_breakdown.map((c, idx) => (
            <div key={c.category} style={catCard} data-testid={`report-card-${c.category}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 700, fontSize: 17, color: NAVY }}>{c.label}</div>
                <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 34, letterSpacing: 1, color: NAVY }}>
                  {fmtDollars(c.amount_cents)}<span style={{ fontSize: 14, color: "#5b5e69" }}>/yr</span>
                </div>
              </div>
              <div style={{ color: "#5b5e69", fontSize: 14, marginTop: 4 }}>{c.headline_copy}</div>
              <button onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)} style={chev} data-testid={`report-expand-${c.category}`}>
                {expandedIdx === idx ? "− Hide the math" : "+ How we calculated this"}
              </button>
              {expandedIdx === idx && (
                <div style={{ marginTop: 10, padding: 12, background: BG, borderRadius: 10, color: NAVY, fontSize: 13, lineHeight: 1.6 }} data-testid={`report-formula-${c.category}`}>
                  {c.formula_plain}
                </div>
              )}
            </div>
          ))}

          <div style={{ height: 2, background: GOLD, margin: "20px 0" }} />
          <div style={{ background: NAVY, color: GOLD, padding: 22, borderRadius: 14, textAlign: "center" }} data-testid="report-total">
            <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>ESTIMATED ANNUAL REVENUE LEFT ON THE TABLE</div>
            <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 64, letterSpacing: 1, lineHeight: 1 }}>
              {fmtDollars(data.total_annual_leakage_cents)}
            </div>
            {data.capped_for_credibility && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#cbd5e1" }}>(Capped at $150k — your real number is likely higher.)</div>
            )}
          </div>

          <h3 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, color: NAVY, fontSize: 22, marginTop: 22 }}>
            COMPARE TO A NEARNOW SUBSCRIPTION
          </h3>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }} data-testid="report-plan-compare">
            <thead style={{ background: BG }}>
              <tr>
                <th style={planTh}>NearNow Plan</th>
                <th style={planTh}>Annual Cost</th>
                <th style={planTh}>You Keep</th>
              </tr>
            </thead>
            <tbody>
              {data.plan_compare.map((p) => (
                <tr key={p.plan} style={{ borderTop: "1px solid #e5e7ee" }}>
                  <td style={planTd}>{p.plan}</td>
                  <td style={planTd}>{fmtDollars(p.annual_cents)}/yr</td>
                  <td style={{ ...planTd, color: "#1A7A4A", fontWeight: 700 }}>{fmtDollars(p.kept_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <button onClick={ctaToSignup} style={ctaButton} data-testid="report-cta-signup">
            CLAIM MY FOUNDING CONTRACTOR SPOT →
          </button>
          {data.rep_slug && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: "#FFF8E5", borderRadius: 10, fontSize: 13, color: "#5b5e69" }}>
              ✓ Your discount code from <strong>{data.rep_slug.toUpperCase()}</strong> will be applied automatically at checkout.
            </div>
          )}
        </div>

        <p style={{ color: "#9ca3af", fontSize: 12, marginTop: 28, textAlign: "center" }}>
          Estimates based on your inputs and Rhode Island home-service industry benchmarks. Actual results vary by business.
        </p>
      </div>
    </div>
  );
}

const pageWrap = { background: BG, minHeight: "100vh", padding: "24px 16px 60px" };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 1.5, fontSize: 36, color: NAVY, margin: "4px 0" };
const card = { background: "#fff", border: `2px solid ${GOLD}`, borderRadius: 18, padding: 22 };
const catCard = { background: "#fff", border: "1px solid #e5e7ee", borderRadius: 12, padding: 16, marginBottom: 12 };
const chev = { marginTop: 8, background: "transparent", border: "none", color: NAVY, fontWeight: 700, letterSpacing: 1, fontSize: 13, cursor: "pointer", padding: 0 };
const planTh = { textAlign: "left", padding: "10px 12px", color: NAVY, fontSize: 11, letterSpacing: 1.5, fontWeight: 700 };
const planTd = { padding: "10px 12px", color: NAVY, fontSize: 14 };
const ctaButton = { display: "block", width: "100%", marginTop: 18, background: GOLD, color: NAVY, border: "none", padding: "22px 24px", borderRadius: 14, fontWeight: 700, letterSpacing: 2, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", fontSize: 18 };
